import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, adminHeaders } from "@/lib/apiClient";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setShowCurrent(false);
    setShowNext(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.trim().length < 4) {
      toast.error("La nuova password deve avere almeno 4 caratteri");
      return;
    }
    if (next !== confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await api.post(
        "/auth/change-password",
        { current_password: current, new_password: next },
        { headers: adminHeaders() }
      );
      // keep the unlocked session valid with the new password
      localStorage.setItem("rcv_admin_pwd", next);
      toast.success("Password aggiornata");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel cambio password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-[#0b0b0b] border-border" data-testid="change-password-dialog">
        <DialogHeader>
          <div className="h-12 w-12 bg-rugby-yellow/10 border border-rugby-yellow/30 flex items-center justify-center mb-2">
            <KeyRound className="h-5 w-5 text-rugby-yellow" />
          </div>
          <DialogTitle className="font-head uppercase tracking-tight text-2xl">
            Reimposta Password
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Inserisci la password attuale e quella nuova.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Password attuale
            </Label>
            <div className="relative">
              <Input
                type={showCurrent ? "text" : "password"}
                data-testid="current-password-input"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="bg-black/50 border-border focus-visible:ring-rugby-yellow pr-10"
                autoFocus
              />
              <button
                type="button"
                data-testid="toggle-current-password"
                onClick={() => setShowCurrent((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-rugby-yellow"
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Nuova password
            </Label>
            <div className="relative">
              <Input
                type={showNext ? "text" : "password"}
                data-testid="new-password-input"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="bg-black/50 border-border focus-visible:ring-rugby-yellow pr-10"
              />
              <button
                type="button"
                data-testid="toggle-new-password"
                onClick={() => setShowNext((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-rugby-yellow"
              >
                {showNext ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Conferma nuova password
            </Label>
            <Input
              type={showNext ? "text" : "password"}
              data-testid="confirm-new-password-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-black/50 border-border focus-visible:ring-rugby-yellow"
            />
          </div>
          <Button
            type="submit"
            data-testid="change-password-submit"
            disabled={loading}
            className="w-full bg-rugby-yellow text-black hover:bg-rugby-yellow/90 font-head uppercase tracking-wide font-bold"
          >
            {loading ? "Aggiornamento..." : "Aggiorna Password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
